/**
 * Creates new cookie or removes cookie with negative expiration
 * @param  key       The key or identifier for the store
 * @param  value     Contents of the store
 * @param  exp       Expiration - creation defaults to 30 days
 */
export function createCookie(key, value, exp = 30) {
  const date = new Date()
  date.setTime(date.getTime() + exp * 24 * 60 * 60 * 1000)
  const expires = `; expires=${date.toUTCString()}`
  document.cookie = `${key}=${value}${expires}; path=/`
}

/**
 * Returns contents of cookie
 * @param  key       The key or identifier for the store
 */
export function readCookie(key): string | null {
  const nameEQ = `${key}=`
  const ca = document.cookie.split(';')
  for (let i = 0, max = ca.length; i < max; i += 1) {
    let c = ca[i]
    while (c.charAt(0) === ' ') {
      c = c.substring(1, c.length)
    }
    if (c.indexOf(nameEQ) === 0) {
      return c.substring(nameEQ.length, c.length)
    }
  }
  return null
}

export function deleteUserSessionToken() {
  SimpleStore.delete('token')
  SimpleStore.delete('ddweb_token')
}

/**
 * Simple localStorage with Cookie Fallback
 * v.1.0.0
 *
 * USAGE:
 * ----------------------------------------
 * Set New / Modify:
 *   SimpleStore.set('my_key', 'some_value');
 *
 * Retrieve:
 *   SimpleStore.get('my_key');
 *
 * Delete / Remove:
 *   SimpleStore.delete('my_key');
 */
const SimpleStore = (() => {
  function isLocalStorageSupported() {
    try {
      localStorage.setItem('1', '1')
      localStorage.removeItem('1')
      return true
    } catch (error) {
      return false
    }
  }

  let lsSupport = false

  // Check for native support
  if (typeof localStorage !== 'undefined' && isLocalStorageSupported()) {
    lsSupport = true
  }

  function set(key, value) {
    let val = value

    // Convert object values to JSON
    if (typeof value === 'object') {
      val = JSON.stringify(value)
    }

    // Set the store
    if (lsSupport) {
      // Native support
      localStorage.setItem(key, val)
    } else {
      // Use Cookie
      createCookie(key, val, 30)
    }
  }

  function get(key) {
    let data

    // Get value
    if (lsSupport) {
      // Native support
      data = localStorage.getItem(key)
    } else {
      // Use cookie
      data = readCookie(key)
    }

    if (data === null) {
      data = readCookie(key)
    }

    // Try to parse JSON...
    try {
      data = JSON.parse(data)
    } catch (e) {
      /* whatever */
    }

    return data
  }

  function deleteItem(key) {
    if (lsSupport) {
      // Native support
      localStorage.removeItem(key)
    } else {
      // Use cookie
      createCookie(key, '', -1)
    }
  }

  return { set, get, delete: deleteItem }
})()

export default SimpleStore
